import React from 'react'
import ReactDOM from 'react-dom/client'
import App from './App.tsx'
import { ErrorBoundary } from './components/ErrorBoundary.tsx'
import { ExtensionDownload } from './components/ExtensionDownload.tsx'
import { PrivacyPolicy } from './components/PrivacyPolicy.tsx'
import './index.css'

type AppMode = 'edit' | 'view';

function hasPublishedHash(hash: string): boolean {
  const normalized = hash.startsWith('#') ? hash.slice(1) : hash;
  return normalized.startsWith('json=');
}

function normalizePath(pathname: string): string {
  if (pathname === '/' || pathname === '') return '/edit';
  if (pathname === '/view') return '/view';
  if (pathname === '/edit') return '/edit';
  if (pathname === '/privacy-policy') return '/privacy-policy';
  if (pathname === '/extension') return '/extension';
  return '/edit';
}

function resolveMode(pathname: string, hash: string): AppMode | 'privacy' | 'extension' {
  if (hasPublishedHash(hash)) return 'view';
  const norm = normalizePath(pathname);
  if (norm === '/privacy-policy') return 'privacy';
  if (norm === '/extension') return 'extension';
  return norm === '/view' ? 'view' : 'edit';
}

function AppShell() {
  const [{ pathname, hash }, setLocation] = React.useState(() => ({
    pathname: window.location.pathname,
    hash: window.location.hash,
  }));

  React.useEffect(() => {
    const desiredPath = normalizePath(pathname);
    if (pathname !== desiredPath) {
      window.history.replaceState({}, '', `${desiredPath}${hash || ''}`);
      setLocation({ pathname: desiredPath, hash: window.location.hash });
    }
  }, [pathname, hash]);

  React.useEffect(() => {
    const onLocation = () => {
      setLocation({ pathname: window.location.pathname, hash: window.location.hash });
    };
    window.addEventListener('popstate', onLocation);
    window.addEventListener('hashchange', onLocation);
    return () => {
      window.removeEventListener('popstate', onLocation);
      window.removeEventListener('hashchange', onLocation);
    };
  }, []);

  const mode = resolveMode(pathname, hash);
  if (mode === 'privacy') return <PrivacyPolicy />;
  if (mode === 'extension') return <ExtensionDownload />;
  return <App mode={mode} />;
}

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <ErrorBoundary>
      <AppShell />
    </ErrorBoundary>
  </React.StrictMode>,
) 
